import axiosWrapper from './axiosWrapper';
import { getCharacters, getFamilies } from '../cache/dataClient';
import cacheSync from '../cache/cacheSync';

const characterService = {
  // Get all characters for a project (offline-first)
  async getCharacters(projectId, options = {}) {
    const { useCache = true } = options;
    const result = await getCharacters(projectId, { useCache });
    if (result.error && !result.fromCache) {
      return { data: null, error: result.error };
    }

    // Return in format expected by existing code
    return {
      data: { characters: result.data || [] },
      error: null,
      fromCache: result.fromCache,
    };
  },

  // Get a single character by ID
  async getCharacterById(projectId, characterId) {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/characters/${characterId}`,
    });
    return response;
  },

  // Create a new character
  async createCharacter(projectId, characterData) {
    const response = await axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/characters`,
      data: characterData,
    });
    // Update cache after successful creation
    if (!response.error) {
      cacheSync.syncAfterMutation('characters', response, 'create').catch((err) => {
        console.warn('[characterService] Error updating cache after create:', err);
      });
    }
    return response;
  },

  // Update an existing character
  async updateCharacter(projectId, characterId, characterData) {
    const response = await axiosWrapper({
      method: 'put',
      path: `projects/${projectId}/characters/${characterId}`,
      data: characterData,
    });
    // Update cache after successful update
    if (!response.error) {
      cacheSync.syncAfterMutation('characters', response, 'update').catch((err) => {
        console.warn('[characterService] Error updating cache after update:', err);
      });
    }
    return response;
  },

  // Delete a character
  async deleteCharacter(projectId, characterId) {
    const response = await axiosWrapper({
      method: 'delete',
      path: `projects/${projectId}/characters/${characterId}`,
    });
    // Update cache after successful delete
    if (!response.error) {
      cacheSync.syncAfterMutation('characters', { id: characterId }, 'delete').catch((err) => {
        console.warn('[characterService] Error updating cache after delete:', err);
      });
    }
    return response;
  },

  // Get all families for a project (offline-first)
  async getFamilies(projectId, options = {}) {
    const { useCache = true } = options;
    const result = await getFamilies(projectId, { useCache });
    if (result.error && !result.fromCache) {
      return { data: null, error: result.error };
    }

    // Return in format expected by existing code
    return {
      data: { families: result.data || [] },
      error: null,
      fromCache: result.fromCache,
    };
  },

  // Get a single family by ID
  async getFamilyById(projectId, familyId) {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/families/${familyId}`,
    });
    return response;
  },

  // Create a new family
  async createFamily(projectId, familyData) {
    const response = await axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/families`,
      data: familyData,
    });
    // Update cache after successful creation
    if (!response.error) {
      cacheSync.syncAfterMutation('families', response, 'create').catch((err) => {
        console.warn('[characterService] Error updating cache after create:', err);
      });
    }
    return response;
  },

  // Update an existing family
  async updateFamily(projectId, familyId, familyData) {
    const response = await axiosWrapper({
      method: 'put',
      path: `projects/${projectId}/families/${familyId}`,
      data: familyData,
    });
    // Update cache after successful update
    if (!response.error) {
      cacheSync.syncAfterMutation('families', response, 'update').catch((err) => {
        console.warn('[characterService] Error updating cache after update:', err);
      });
    }
    return response;
  },

  // Delete a family
  async deleteFamily(projectId, familyId) {
    const response = await axiosWrapper({
      method: 'delete',
      path: `projects/${projectId}/families/${familyId}`,
    });
    // Update cache after successful delete
    if (!response.error) {
      cacheSync.syncAfterMutation('families', { id: familyId }, 'delete').catch((err) => {
        console.warn('[characterService] Error updating cache after delete:', err);
      });
    }
    return response;
  },
};

export default characterService;
